import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_PATHS } from '../config/api-paths';
import { SmeRecurringApi } from '../tokens/sme-recurring.token';
import {
  RecurringRule,
  RecurringRuleCreateRequest,
  RecurringRuleUpdateRequest,
} from '../models/sme-recurring.model';

/**
 * REST-only implementation of `SmeRecurringApi` (EM-358, C5).
 *
 * Calls the embedded recurring-rule surface (PRD §5.2 P-08, P-06):
 *   GET    /services/embedded/api/recurring
 *   POST   /services/embedded/api/recurring
 *   PATCH  /services/embedded/api/recurring/{id}
 *   DELETE /services/embedded/api/recurring/{id}
 *   POST   /services/embedded/api/recurring/{id}/pause
 *   POST   /services/embedded/api/recurring/{id}/resume
 *
 * Rides the existing Keycloak bearer (attached by `authInterceptor`). Rule 07 / Rule 10 — no Mock
 * fallback; Playwright specs intercept via `page.route()`. Errors propagate so callers surface the
 * RFC-7807 detail via `extractBackendErrorMessage` (Rule 09).
 */
@Injectable()
export class SmeRecurringRestService implements SmeRecurringApi {
  private readonly http = inject(HttpClient);
  private readonly base = API_PATHS.RECURRING;

  list(): Observable<RecurringRule[]> {
    return this.http.get<RecurringRule[]>(this.base);
  }

  create(payload: RecurringRuleCreateRequest): Observable<RecurringRule> {
    return this.http.post<RecurringRule>(this.base, payload);
  }

  update(id: number, payload: RecurringRuleUpdateRequest): Observable<RecurringRule> {
    return this.http.patch<RecurringRule>(`${this.base}/${id}`, payload);
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}`);
  }

  pause(id: number): Observable<RecurringRule> {
    return this.http.post<RecurringRule>(`${this.base}/${id}/pause`, {});
  }

  resume(id: number): Observable<RecurringRule> {
    return this.http.post<RecurringRule>(`${this.base}/${id}/resume`, {});
  }
}
